import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import { abbreviateNumber } from "../../../../../utils/utils";

const calculateRawData = (data) => {
  const tradeSum = data[0].trade + data[1].trade + data[2].trade;

  return [
    {
      label: "Unlocked",
      value: (data[0].trade / tradeSum) * 100,
      trade: data[0].trade,
      color: "#007DFF",
    },
    {
      label: "Locked",
      value: (data[1].trade / tradeSum) * 100,
      trade: data[1].trade,
      color: "#FFFFFF33",
    },
    {
      label: "Untracked",
      value: (data[2].trade / tradeSum) * 100,
      trade: data[2].trade,
      color: "#09141E",
    },
  ];
};

interface UnlockChartProps {
  lockedSupply: number;
  unlockedSupply: number;
  untrackedSupply: number;
  tokenSymbol: string;
}

export const UnlockChart = ({
  lockedSupply,
  unlockedSupply,
  untrackedSupply,
  tokenSymbol,
}: UnlockChartProps) => {
  const chartRef = useRef<SVGSVGElement | null>(null);

  const preFormattedData = [
    { label: "Unlocked", trade: unlockedSupply, color: "#007DFF" },
    { label: "Locked", trade: lockedSupply, color: "#FFFFFF33" },
    { label: "Untracked", trade: untrackedSupply, color: "#09141E" },
  ];

  const rawData = calculateRawData(preFormattedData);

  const data = rawData.filter((item) => item.label !== "Untracked");

  useEffect(() => {
    if (!chartRef.current) return;

    const width = 120;
    const height = 120;
    const radius = Math.min(width, height) / 2;

    const svg = d3.select(chartRef.current);
    svg.selectAll("*").remove();

    const chart = svg
      .attr("width", width)
      .attr("height", height)
      .append("g")
      .attr("transform", `translate(${width / 2},${height / 2})`);

    const pie = d3
      .pie<{ label: string; value: number; color: string }>()
      .value((d) => d.value)
      .sort(null)
      .startAngle(0)
      .endAngle(2 * Math.PI);

    const arc = d3
      .arc<d3.PieArcDatum<{ label: string; value: number; color: string }>>()
      .innerRadius(radius * 0.82)
      .outerRadius(radius);

    chart
      .selectAll("path")
      .data(pie(data))
      .enter()
      .append("path")
      .attr("d", arc as any)
      .attr("fill", (d) => d.data.color);
  }, [data]);

  return (
    <div className="flex w-full p-5 gap-3 bg-[#55A6FB1A] rounded-xl">
      {/* Chart with text */}
      <div className="relative">
        <svg ref={chartRef} className="relative"></svg>
        <div className="absolute h-[120px] w-[120px] top-0 left-0 flex flex-col justify-center items-center">
          <p className="font-bold text-xl">{data[1].value.toFixed(2)}%</p>
          <span className="font-normal text-xs text-white opacity-50">
            Locked
          </span>
        </div>
      </div>
      {/* Unlock Chart Legend */}
      <div className="flex-grow flex flex-col justify-center gap-[10px]">
        <div className="flex justify-between flex-wrap gap-1">
          <div className="flex gap-1">
            <div className="w-4 h-4 rounded-[4px] bg-[#FFFFFF33]" />
            <p className="font-normal text-white opacity-50 text-xs">
              {rawData[1].label}
            </p>
          </div>
          <p className="font-medium text-sm">
            {abbreviateNumber(rawData[1].trade)} {tokenSymbol}
          </p>
        </div>
        <div className="flex justify-between flex-wrap gap-1">
          <div className="flex gap-1">
            <div className="w-4 h-4 rounded-[4px] bg-[#007DFF]" />
            <p className="font-normal text-white opacity-50 text-xs">
              {rawData[0].label}
            </p>
          </div>
          <p className="font-medium text-sm">
            {abbreviateNumber(rawData[0].trade)} {tokenSymbol}
          </p>
        </div>
        <div className="flex justify-between flex-wrap gap-1">
          <div className="flex gap-1">
            <div className="w-4 h-4 rounded-[4px] bg-[#09141E]" />
            <p className="font-normal text-white opacity-50 text-xs">
              {rawData[2].label}
            </p>
          </div>
          <p className="font-medium text-sm">
            {abbreviateNumber(rawData[2].trade)} {tokenSymbol}
          </p>
        </div>
      </div>
    </div>
  );
};
